"use client";
import React from "react";
import { motion } from "framer-motion";
import { Caveat } from "next/font/google";
import Image from "next/image";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

/* ─────────────────────────────────────────────
   1. Your online storefront, 24/7
   ───────────────────────────────────────────── */
export function ArtsCraftsStorefront({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Title */}
        <div className="text-center mb-14">
          <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight ${caveat.className}`}>
            Your online storefront,{" "}
            <span className="x_wd_red_highlight_02">24/7</span>
          </h2>
        </div>

        {/* 3-Column Descriptions */}
        <div className="row g-5 mb-12 justify-content-center">
          <div className="col-lg-4">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white">Showcase your products.</strong> Build a stunning website using Odoo's AI-based website builder, then add your products — it only takes minutes to start selling.
            </p>
          </div>
          <div className="col-lg-4">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white">Shipping options.</strong> Odoo is compatible with shipping connectors from all of the biggest players. Add click-and-collect services to make shopping even easier.
            </p>
          </div>
          <div className="col-lg-4">
            <p className="mb-0 text-xl text-gray-300 leading-relaxed">
              <strong className="text-white">Secure online shopping.</strong> All data and payments are secure with us.
            </p>
          </div>
        </div>

        {/* eCommerce Mockup Card */}
        <div className="row justify-content-center">
          <div className="col-lg-10 position-relative">
            {/* Red Paint Bucket Icon floating top-right */}
            <div 
              className="d-none d-lg-inline position-absolute z-30 select-none pointer-events-none"
              style={{ width: 90, top: -20, right: -30, transform: "rotate(10deg)" }}
            >
              <Image
                src="/odoo/icons/red_paint-bucket-1.svg"
                width={90}
                height={90}
                className="object-contain"
                alt=""
              />
            </div>
            {/* Down arrow */}
            <div className="flex justify-center mb-6">
              <Image
                src="/odoo/arrows/arrow_sm_03.svg"
                style={{ filter: "brightness(0) invert(1)", opacity: 0.4 }}
                width={50}
                height={50}
                className="mx-auto object-contain"
                alt=""
              />
            </div>
            {/* eCommerce screenshot */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.6)] overflow-hidden hover:scale-[1.005] transition-transform duration-300 bg-[#151517] relative"
            >
              <Image
                src="/odoo/industries/arts-crafts/ecommerce.webp"
                width={1100}
                height={650}
                sizes="(max-width: 1024px) 100vw, 80vw"
                className="w-full h-auto object-contain"
                alt="Arts & Crafts eCommerce store"
              />
            </motion.div>
          </div>
        </div>

        {/* Persona Callout */}
        <div className="w-full flex justify-center mt-16 select-none pointer-events-none">
          <div className="d-flex align-items-center justify-content-center gap-4">
            <div className="d-none d-md-inline-block rounded-full border border-white/20 shadow-lg overflow-hidden w-[60px] h-[60px]">
              <Image
                width={60}
                height={60}
                alt=""
                src="/odoo/snippets/s_wd_persona/christina.webp"
                className="object-cover"
              />
            </div>
            <div className="bg-white/10 border border-white/20 rounded-full shadow-2xl p-4 px-8 backdrop-blur-md">
              <i className="text-white text-base font-semibold">Shipping has never been easier!</i>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   2. All-in-one Point of Sale
   ───────────────────────────────────────────── */
export function ArtsCraftsPOS({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Heading */}
        <div className="d-flex align-items-center justify-content-lg-center gap-4 mb-5">
          <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight ${caveat.className}`}>
            <span className="x_wd_green_highlight_03">All-in-one</span>{" "}Point of Sale
          </h2>
          <div 
            className="d-none d-lg-block rotate-90 mt-n6 select-none pointer-events-none"
            style={{ width: 80, filter: "brightness(0) invert(1)", opacity: 0.3 }}
          >
            <Image
              src="/odoo/graphics/corner_4.svg"
              width={80}
              height={80}
              className="object-contain"
              alt=""
            />
          </div>
        </div>

        <div className="row justify-content-center mb-12">
          <div className="col-lg-9 col-xl-7 text-center">
            <p className="text-gray-300 text-xl leading-relaxed">
              <strong className="text-white">PoS that has it all.</strong> So intuitive that anyone can master it in minutes, yet packed with a range of advanced options.
            </p>
          </div>
        </div>

        {/* POS Screenshot with Annotation Arrows */}
        <div className="row justify-content-center">
          <div className="col-lg-8 position-relative">

            {/* Left Annotations */}
            <div className="hidden lg:block absolute left-[-210px] top-0 w-[180px] text-right z-30 pointer-events-none select-none space-y-14">
              <div>
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Discounts</h4>
                <div className="flex items-center justify-end gap-2">
                  <span className="text-xs text-gray-400">On items & orders</span>
                  <Image src="/odoo/arrows/grey_arrow_sm_03.svg"
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    width={20}
                    height={20}
                    className="rotate-300 w-5 h-auto object-contain" alt="" />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Loyalty programs</h4>
                <div className="flex items-center justify-end gap-2">
                  <span className="text-xs text-gray-400">Reward your customers</span>
                  <Image src="/odoo/arrows/grey_arrow_sm_02.svg"
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    width={20}
                    height={20}
                    className="rotate-320 w-5 h-auto object-contain" alt="" />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Multiple payment methods</h4>
                <div className="flex items-center justify-end gap-2">
                  <span className="text-xs text-gray-400">Cash, card & more</span>
                  <Image src="/odoo/arrows/grey_arrow_sm_03.svg"
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    width={20}
                    height={20}
                    className="rotate-300 w-5 h-auto object-contain" alt="" />
                </div>
              </div>
            </div>

            {/* Right Annotations */}
            <div className="hidden lg:block absolute right-[-210px] top-0 w-[180px] text-left z-30 pointer-events-none select-none space-y-14">
              <div>
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Reliable online & offline</h4>
                <div className="flex items-center gap-2">
                  <Image src="/odoo/arrows/grey_arrow_sm_03.svg"
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    width={20}
                    height={20}
                    className="rotate-60 w-5 h-auto object-contain" alt="" />
                  <span className="text-xs text-gray-400">Always works</span>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Filter by product categories</h4>
                <div className="flex items-center gap-2">
                  <Image src="/odoo/arrows/grey_arrow_sm_02.svg"
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    width={20}
                    height={20}
                    className="rotate-140 w-5 h-auto object-contain" alt="" />
                  <span className="text-xs text-gray-400">Quick product browse</span>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-bold text-sky-400 uppercase tracking-wider mb-1">Intuitive display</h4>
                <div className="flex items-center gap-2">
                  <Image src="/odoo/arrows/grey_arrow_sm_03.svg"
                    style={{ filter: "brightness(0) invert(1)", opacity: 0.35 }}
                    width={20}
                    height={20}
                    className="rotate-130 w-5 h-auto object-contain" alt="" />
                  <span className="text-xs text-gray-400">Easy for anyone</span>
                </div>
              </div>
            </div>

            {/* Main POS Screenshot */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.6)] overflow-hidden hover:scale-[1.005] transition-transform duration-300 bg-[#151517] relative"
            >
              <Image
                src="/odoo/industries/arts-crafts/pos.webp"
                width={850}
                height={550}
                sizes="(max-width: 1024px) 100vw, 60vw"
                className="w-full h-auto object-contain"
                alt="Arts & Crafts Point of Sale"
              />
            </motion.div>

            {/* Mobile Callout */}
            <div className="block lg:hidden mt-8 grid grid-cols-2 gap-4 bg-white/[0.02] border border-white/5 rounded-2xl p-6 text-sm">
              {["Discounts", "Loyalty programs", "Multiple payment methods", "Reliable online & offline", "Filter by category", "Intuitive display"].map(f => (
                <div key={f}>
                  <span className="font-bold text-sky-400 block mb-1">✓ {f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   3. Keep your customers coming back
   ───────────────────────────────────────────── */
export function ArtsCraftsLoyalty({ section }) {
  const programs = [
    { label: "Coupons" },
    { label: "Loyalty cards" },
    { label: "Promotions" },
    { label: "eWallet" },
    { label: "Gift cards" },
  ];

  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="row align-items-center g-5">

          {/* Left: Text */}
          <div className="col-lg-5 pb-5">
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 ${caveat.className}`}>
              Keep your customers coming back
            </h2>
            <p className="text-gray-300 leading-relaxed text-xl">
              <strong className="text-white">Run promotion programs and/or memberships</strong> for your most loyal customers! For example, offer a 10% discount to your Gold members.
            </p>
          </div>

          {/* Right: Loyalty Programs Grid */}
          <div className="col-lg-6 offset-lg-1">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-center">
              {programs.map((prog) => (
                <motion.div
                  key={prog.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="bg-white/[0.04] border border-white/10 rounded-2xl p-6 hover:bg-white/[0.07] hover:border-[#f1833b]/30 transition-all group"
                >
                  <Image
                    src="/odoo/icons/star.svg"
                    width={64}
                    height={64}
                    className="mx-auto mb-3 group-hover:scale-110 transition-transform object-contain"
                    alt={prog.label}
                    style={{ filter: "hue-rotate(20deg) saturate(1.5)" }}
                  />
                  <h5 className="text-white font-bold text-base leading-tight">{prog.label}</h5>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   4. Inventory management
   ───────────────────────────────────────────── */
export function ArtsCraftsInventory({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">

        {/* Top Row: Heading + Main mockup */}
        <div className="row g-5 align-items-center pb-10">
          <div className="col-xl-5">
            <h2 className={`text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6 ${caveat.className}`}>
              <span className="x_wd_blue_highlight_01">Inventory</span> management
            </h2>
            <p className="text-gray-300 leading-relaxed text-xl">
              <strong className="text-white">Product search.</strong> Find products by name, code, or category to optimize order fulfillment. Track by lots, serial numbers, and more.
            </p>
          </div>

          <div className="col-xl-6 offset-xl-1 position-relative py-5 py-lg-7">
            {/* Loop arrow */}
            <div 
              className="z-30 d-none d-lg-block position-absolute top-50 start-0 flip rotate-30 mt-8 ms-n8 select-none pointer-events-none"
              style={{ width: 150, filter: "brightness(0) invert(1)", opacity: 0.3, transform: "scaleX(-1) rotate(30deg)" }}
            >
              <Image
                src="/odoo/arrows/arrow_loop_02.svg"
                width={150}
                height={150}
                className="object-contain"
                alt=""
              />
            </div>
            {/* Background dome shape */}
            <div className="absolute top-0 bottom-0 left-[15%] right-[-100vw] bg-white/[0.03] rounded-l-[180px] md:rounded-l-[240px] -z-10 pointer-events-none select-none" />

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.55)] overflow-hidden hover:scale-[1.01] transition-transform duration-300 bg-[#151517] relative"
            >
              <Image
                src="/odoo/industries/arts-crafts/inventory.webp"
                width={850}
                height={550}
                sizes="(max-width: 1024px) 100vw, 60vw"
                className="w-full h-auto object-contain"
                alt="Inventory management"
              />
            </motion.div>
          </div>
        </div>

        {/* Bottom Row: 2 sub-images */}
        <div className="row g-5">
          <div className="col-lg-6 position-relative pb-5">
            <p className="text-gray-300 mb-5 text-xl leading-relaxed">
              <strong className="text-white">Real-time stock tracking.</strong> Monitor inventory levels closely to avoid stockouts and overstocking.
            </p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.45)] overflow-hidden hover:scale-[1.01] transition-transform duration-300 bg-[#151517] relative"
            >
              <Image
                src="/odoo/industries/arts-crafts/inventory-2.webp"
                width={650}
                height={450}
                className="w-full h-auto object-contain"
                alt="Real-time stock tracking"
              />
            </motion.div>
          </div>

          <div className="col-lg-6 position-relative pb-5">
            <p className="text-gray-300 mb-5 text-xl leading-relaxed">
              <strong className="text-white">Automated reordering.</strong> Set up automated purchase orders to ensure timely replenishment.
            </p>
            {/* Sync icon */}
            <div 
              className="d-none d-lg-inline position-absolute top-0 end-0 select-none pointer-events-none"
              style={{ height: 70, transform: "rotate(20deg)", opacity: 0.85 }}
            >
              <Image
                src="/odoo/icons/blue_sync.svg"
                width={70}
                height={70}
                className="object-contain"
                alt=""
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.45)] overflow-hidden hover:scale-[1.01] transition-transform duration-300 bg-[#151517] relative"
            >
              <Image
                src="/odoo/industries/arts-crafts/inventory-3.webp"
                width={650}
                height={450}
                className="w-full h-auto object-contain"
                alt="Automated reordering"
              />
            </motion.div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────
   5. Purchasing made easy
   ───────────────────────────────────────────── */
export function ArtsCraftsPurchasing({ section }) {
  return (
    <section className="overflow-visible py-20 bg-transparent text-white relative font-sans w-full z-10">
      <div className="container mx-auto px-6 sm:px-12 lg:px-24 max-w-[1400px] relative z-10">
        <div className="row align-items-center justify-content-between g-5">

          {/* Left: Text */}
          <div className="col-lg-4 pb-5">
            <h2 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 ${caveat.className}`}>
              <span className="x_wd_yellow_highlight_bold_04">Purchasing</span> made easy
            </h2>
            <p className="text-gray-300 leading-relaxed text-xl">
              <strong className="text-white">In a few clicks,</strong> you can choose the vendor, check if it's a consignee, compare product prices, and send your purchase order!
            </p>
          </div>

          {/* Right: Overlapping Mockups */}
          <div className="col-lg-7 offset-lg-1 position-relative pb-5">
            {/* Star decoration */}
            <div 
              className="d-none d-lg-inline position-absolute bottom-0 start-0 z-30 select-none pointer-events-none"
              style={{ height: 110 }}
            >
              <Image
                src="/odoo/graphics/star_down_left.svg"
                width={110}
                height={110}
                className="object-contain"
                alt=""
              />
            </div>

            {/* Background (back) mockup */}
            <div className="absolute top-[5%] left-[0%] w-[60%] z-10">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.4)] overflow-hidden bg-[#151517] relative"
              >
                <Image
                  src="/odoo/industries/arts-crafts/request-quotation-2.webp"
                  width={600}
                  height={400}
                  className="w-full h-auto object-contain"
                  alt="Purchase quotation background"
                />
              </motion.div>
            </div>

            {/* Foreground mockup */}
            <div className="relative ml-[25%] mt-[8%] z-20 pb-10">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.55)] overflow-hidden hover:scale-[1.01] transition-transform duration-300 bg-[#151517] relative"
              >
                <Image
                  src="/odoo/industries/arts-crafts/request-quotation.webp"
                  width={650}
                  height={450}
                  className="w-full h-auto object-contain"
                  alt="Purchase request quotation"
                />
              </motion.div>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
